import { useDispatch } from 'react-redux';

import FilesClient from 'src/clients/FilesClient';
import { downloadFiles } from 'src/store/files/actions';
import {
  alertSnackbar,
  completeProgress,
  resetProgressValue,
  setProgressValue,
} from 'src/store/ui/actions';
import { S3Utils } from 'src/utils';

export const useProgressDownload = (
  getProgress: typeof FilesClient.getDownloadProgress = FilesClient.getDownloadProgress,
) => {
  const dispatch = useDispatch();

  /**
   * call api to get download job progress
   * if job is completed, download file
   * if job failed, show the failure reason and stop polling
   * if job is still running, set progress bar value and set timeout to call api again
   * @param downloadFileKey file key to download
   */
  const updateDownloadProgress = async (
    downloadFileKey: string,
    entityId?: string,
    channelId?: string,
    path?: string,
  ) => {
    const downloadProgress = await getProgress(downloadFileKey);

    if (
      downloadProgress.status === 'FAILED' ||
      downloadProgress.entityStatus === 'FAILED'
    ) {
      dispatch(completeProgress());
      dispatch(
        alertSnackbar({
          errorMessage:
            downloadProgress.additionalFields?.failureReason ||
            'Your download failed. Please try again or contact support.',
        }),
      );
      return;
    }
    if (
      downloadProgress.status === 'ENABLED' ||
      downloadProgress.entityStatus === 'ENABLED'
    ) {
      const zippedFileKey = downloadFileKey.split('/').slice(2).join('/');
      const signedBundleURL = (
        await S3Utils.getFile(zippedFileKey, {
          level: 'protected',
        })
      ).toString();
      dispatch(completeProgress());
      dispatch(
        downloadFiles([
          {
            id: entityId,
            fileUrl: signedBundleURL,
            fileKey: zippedFileKey,
            fileName: downloadProgress.fields.bundleName,
            emitAuditEvent: true,
            channelId,
            path,
          },
        ]),
      );
    } else {
      // While the worker is still in its expansion phase, totalFiles is 0 —
      // avoid dividing by zero so the bar sits at 0% instead of flashing NaN.
      const totalFiles = downloadProgress.additionalFields?.totalFiles ?? 0;
      const downloadedFiles =
        downloadProgress.additionalFields?.downloadedFiles ?? 0;
      const pct =
        totalFiles > 0 ? Math.round((downloadedFiles / totalFiles) * 100) : 0;
      dispatch(setProgressValue(pct));
      setTimeout(() => {
        updateDownloadProgress(downloadFileKey, entityId, channelId, path);
      }, 500);
    }
  };

  const startZipDownload = (
    downloadFileKey: string,
    numFiles: number,
    entityId?: string,
    channelId?: string,
    path?: string,
  ) => {
    if (downloadFileKey === '') {
      return;
    }
    dispatch(resetProgressValue());
    dispatch(
      alertSnackbar(
        {
          progressMessage: `Zipping ${numFiles} file${numFiles > 1 ? 's' : ''}`,
        },
        true,
      ),
    );
    updateDownloadProgress(downloadFileKey, entityId, channelId, path);
  };

  return startZipDownload;
};
